// Pure discount engine for the sell screen.
// No React, no API, no state: the same input always gives the same priced cart.
// Order: line prices -> filter discounts -> line discounts (automatic, claimed Buy X Get Y)
// -> subtotal -> Pay X Get Free -> coupon -> additional discount -> tax -> totals.
import { isCustomerEligible, isWithinDates } from "./eligibility"
import { allocate, MINOR_PER_MAJOR, percentOf } from "./money"
import {
	AppliedLineDiscount,
	AutomaticDiscount,
	AutomaticOption,
	BuyRequirement,
	BuyXGetYDiscount,
	CalculateCartInput,
	CartLineInput,
	CartMessage,
	ClaimableOffer,
	ClaimResult,
	CouponResult,
	Discount,
	FreeLine,
	Minor,
	PayXGetFreeDiscount,
	PricedCart,
	PricedLine,
	Restriction,
} from "./types"

type Activity = "ACTIVE" | "NOT_STARTED" | "EXPIRED" | "NOT_ELIGIBLE"

const formatAmount = (amount: Minor) => (amount / 100).toFixed(2)

const activityOf = (discount: Discount, customerId: number | null, now: number): Activity => {
	if (!isWithinDates(discount, now)) return discount.startsAt != null && now < discount.startsAt ? "NOT_STARTED" : "EXPIRED"
	return isCustomerEligible(discount, customerId) ? "ACTIVE" : "NOT_ELIGIBLE"
}

const grossOf = (line: CartLineInput): Minor => Math.round(line.unitPrice * line.quantity)

const automaticMatches = (discount: AutomaticDiscount, line: CartLineInput) => {
	switch (discount.scope.type) {
		case "PRODUCT":
			return discount.scope.variantIds.includes(line.variantId)
		case "BRAND":
			return line.brandId === discount.scope.brandId
		case "CATEGORY":
			return line.categoryIds.includes(discount.scope.categoryId)
	}
}

const buyMatches = (requirement: BuyRequirement, line: CartLineInput) =>
	requirement.type === "CATEGORY"
		? line.categoryIds.includes(requirement.categoryId)
		: requirement.variantId != null ? line.variantId === requirement.variantId : line.productId === requirement.productId

// Restrictions are checked against the qualifying lines only
const restrictionMet = (restriction: Restriction, qualifying: CartLineInput[]) => {
	switch (restriction.kind) {
		case "NONE":
			return true
		case "MIN_QUANTITY":
			return qualifying.reduce((sum, line) => sum + line.quantity, 0) >= restriction.min
		case "MIN_AMOUNT":
			return qualifying.reduce((sum, line) => sum + grossOf(line), 0) >= restriction.min
	}
}

const uncappedAutomatic = (discount: AutomaticDiscount, line: CartLineInput): Minor => {
	const gross = grossOf(line)
	const amount = discount.valueType === "PERCENTAGE" ? percentOf(gross, discount.value) : Math.round(discount.value * line.quantity)
	return Math.max(0, Math.min(amount, gross))
}

// Takes the units each buy requirement needs from `pool` (mutates it). Lines in `avoidKey`
// are used last so the get item keeps its units for the "get" side when possible.
const allocateBuyUnits = (requirements: BuyRequirement[], lines: CartLineInput[], pool: Record<string, number>, avoidKey?: string) => {
	for (const requirement of requirements) {
		let needed = requirement.quantity
		const candidates = lines
			.filter((line) => buyMatches(requirement, line) && (pool[line.key] ?? 0) > 0)
			.sort((a, b) => Number(a.key === avoidKey) - Number(b.key === avoidKey))
		for (const line of candidates) {
			if (needed <= 0) break
			const take = Math.min(needed, pool[line.key])
			pool[line.key] -= take
			needed -= take
		}
		if (needed > 0) return false
	}
	return true
}

export const calculateCart = (input: CalculateCartInput): PricedCart => {
	const { customerId, now, config } = input
	const messages: CartMessage[] = []
	const applied = new Set<number>()
	const lines = input.lines
	const paidLines = lines.filter((line) => line.quantity > 0)

	const activity = new Map<number, Activity>()
	input.discounts.forEach((discount) => activity.set(discount.id, activityOf(discount, customerId, now)))
	const isActive = (discount: Discount) => activity.get(discount.id) === "ACTIVE"

	const automatics = input.discounts.filter((d): d is AutomaticDiscount => d.kind === "AUTOMATIC" && isActive(d))
	const buyXGetYs = input.discounts.filter((d): d is BuyXGetYDiscount => d.kind === "BUY_X_GET_Y")
	const payXs = input.discounts.filter((d): d is PayXGetFreeDiscount => d.kind === "PAY_X_GET_FREE" && isActive(d))

	// ---- Automatic: options per line, cashier's choice or the best saving ----
	const optionsByLine: Record<string, AutomaticOption[]> = {}
	const selectedByLine: Record<string, AutomaticOption | null> = {}
	const qualifyingByAutomatic = new Map<number, boolean>()
	automatics.forEach((discount) => {
		qualifyingByAutomatic.set(discount.id, restrictionMet(discount.restriction, paidLines.filter((line) => automaticMatches(discount, line))))
	})
	paidLines.forEach((line) => {
		const options = automatics
			.filter((discount) => automaticMatches(discount, line) && qualifyingByAutomatic.get(discount.id))
			.map((discount) => ({
				discountId: discount.id,
				title: discount.title,
				amount: Math.min(uncappedAutomatic(discount, line), discount.cap ?? Infinity),
			}))
			.filter((option) => option.amount > 0)
			.sort((a, b) => b.amount - a.amount || a.discountId - b.discountId)
		optionsByLine[line.key] = options
		const choice = input.automaticChoice[line.key]
		selectedByLine[line.key] = choice === "NONE" ? null : options.find((option) => option.discountId === choice) ?? options[0] ?? null
	})

	// ---- Claimed Buy X Get Y ----
	const pool: Record<string, number> = {}
	paidLines.forEach((line) => { pool[line.key] = Math.floor(line.quantity) })
	const offerByLine: Record<string, AppliedLineDiscount> = {}
	const claims: ClaimResult[] = []
	const bxgyById = new Map(buyXGetYs.map((discount) => [discount.id, discount]))

	input.claimedOffers.forEach((claim) => {
		const result: ClaimResult = { discountId: claim.discountId, getVariantId: claim.getVariantId, status: "DROPPED", reason: null, getUnits: 0, amount: 0 }
		claims.push(result)
		const discount = bxgyById.get(claim.discountId)
		const title = discount?.title ?? "Offer"
		const state = discount ? activity.get(discount.id) : undefined
		if (!discount || state === "EXPIRED" || state === "NOT_STARTED") {
			result.reason = "INACTIVE"
			messages.push({ severity: "warning", discountId: claim.discountId, text: `"${title}" is no longer active and was removed.` })
			return
		}
		if (state === "NOT_ELIGIBLE") {
			result.reason = "NOT_ELIGIBLE"
			messages.push({ severity: "warning", discountId: discount.id, text: `"${title}" is not available for ${customerId == null ? "walk-in customers" : "this customer"} and was removed.` })
			return
		}
		const option = discount.get.options.find((item) => item.variantId === claim.getVariantId)
		if (!option) {
			result.reason = "INVALID_GET_ITEM"
			messages.push({ severity: "warning", discountId: discount.id, text: `"${title}": the chosen item is no longer part of the offer.` })
			return
		}
		const getLine = paidLines.find((line) => line.variantId === claim.getVariantId)
		if (!getLine) {
			result.reason = "GET_ITEM_NOT_IN_CART"
			messages.push({ severity: "warning", discountId: discount.id, text: `"${title}" removed: ${option.name} is no longer in the cart.` })
			return
		}
		if (offerByLine[getLine.key]) {
			result.reason = "LINE_HAS_OFFER"
			messages.push({ severity: "warning", discountId: discount.id, text: `"${title}" not applied: ${getLine.name} already has an offer.` })
			return
		}
		const trial = { ...pool }
		const buyQualifying = paidLines.filter((line) => discount.buy.some((requirement) => buyMatches(requirement, line)))
		if (!allocateBuyUnits(discount.buy, paidLines, trial, getLine.key) || !restrictionMet(discount.restriction, buyQualifying)) {
			result.reason = "BUY_NOT_MET"
			messages.push({ severity: "warning", discountId: discount.id, text: `"${title}" removed: the buy condition is no longer met. ${option.name} stays at full price.` })
			return
		}
		const units = Math.min(discount.get.quantity, trial[getLine.key] ?? 0)
		if (units <= 0) {
			result.reason = "NO_GET_UNITS"
			messages.push({ severity: "warning", discountId: discount.id, text: `"${title}" needs another ${option.name} in the cart.` })
			return
		}
		const amount = Math.min(percentOf(Math.round(getLine.unitPrice * units), discount.get.percent), grossOf(getLine))
		result.getUnits = units
		result.amount = amount
		// Same line: automatic OR Buy X Get Y, whichever saves more
		const automatic = selectedByLine[getLine.key]
		if (automatic && automatic.amount >= amount) {
			result.status = "NOT_BETTER"
			messages.push({ severity: "info", discountId: discount.id, text: `"${automatic.title}" saves more on ${getLine.name}, so "${title}" is not applied.` })
			return
		}
		trial[getLine.key] -= units
		Object.assign(pool, trial)
		result.status = "APPLIED"
		offerByLine[getLine.key] = { discountId: discount.id, kind: "BUY_X_GET_Y", title, amount }
		applied.add(discount.id)
	})

	// ---- Final line discounts; automatic caps apply across the whole order ----
	const lineDiscount: Record<string, AppliedLineDiscount | null> = {}
	const automaticLines = new Map<number, CartLineInput[]>()
	paidLines.forEach((line) => {
		if (offerByLine[line.key]) {
			lineDiscount[line.key] = offerByLine[line.key]
			return
		}
		const selected = selectedByLine[line.key]
		lineDiscount[line.key] = null
		if (selected) {
			const group = automaticLines.get(selected.discountId) ?? []
			group.push(line)
			automaticLines.set(selected.discountId, group)
		}
	})
	automaticLines.forEach((group, discountId) => {
		const discount = automatics.find((item) => item.id === discountId)!
		const uncapped = group.map((line) => uncappedAutomatic(discount, line))
		const total = uncapped.reduce((sum, amount) => sum + amount, 0)
		const amounts = discount.cap != null && total > discount.cap ? allocate(discount.cap, uncapped) : uncapped
		if (discount.cap != null && total > discount.cap) {
			messages.push({ severity: "info", discountId, text: `"${discount.title}" reached its limit of ${formatAmount(discount.cap)} for this order.` })
		}
		group.forEach((line, index) => {
			if (amounts[index] > 0) {
				lineDiscount[line.key] = { discountId, kind: "AUTOMATIC", title: discount.title, amount: amounts[index] }
				applied.add(discountId)
			}
		})
	})

	// ---- Offers the cashier can still claim, using units not already given to an offer ----
	const claimedIds = new Set(claims.filter((claim) => claim.status !== "DROPPED").map((claim) => claim.discountId))
	const claimableByLine: Record<string, ClaimableOffer[]> = {}
	buyXGetYs.filter((discount) => isActive(discount) && !claimedIds.has(discount.id)).forEach((discount) => {
		const buyQualifying = paidLines.filter((line) => discount.buy.some((requirement) => buyMatches(requirement, line)))
		if (!restrictionMet(discount.restriction, buyQualifying) || !allocateBuyUnits(discount.buy, paidLines, { ...pool })) return
		const options = discount.get.options.filter((option) => option.inStock)
		if (!options.length) return
		const offer: ClaimableOffer = {
			discountId: discount.id,
			title: discount.title,
			buy: discount.buy,
			getType: discount.get.type,
			getName: discount.get.name,
			getQuantity: discount.get.quantity,
			percent: discount.get.percent,
			options,
		}
		buyQualifying.filter((line) => pool[line.key] > 0).forEach((line) => { (claimableByLine[line.key] ??= []).push(offer) })
	})

	// ---- Subtotal after line discounts (free lines are never part of it) ----
	const lineTotal: Record<string, Minor> = {}
	lines.forEach((line) => { lineTotal[line.key] = grossOf(line) - (lineDiscount[line.key]?.amount ?? 0) })
	const subtotalAfterLineDiscounts = paidLines.reduce((sum, line) => sum + lineTotal[line.key], 0)

	// ---- Pay X Get Free: every qualifying discount, every in-stock free product ----
	const freeLines: FreeLine[] = []
	payXs.forEach((discount) => {
		if (subtotalAfterLineDiscounts < discount.threshold) {
			if (input.previousAppliedDiscountIds?.includes(discount.id)) {
				messages.push({ severity: "warning", discountId: discount.id, text: `"${discount.title}" removed: the subtotal is below ${formatAmount(discount.threshold)}.` })
			}
			return
		}
		discount.freeProducts.forEach((product) => {
			if (!product.inStock) {
				messages.push({ severity: "warning", discountId: discount.id, text: `Free item ${product.name} ("${discount.title}") is out of stock and was skipped.` })
				return
			}
			const key = `free:${discount.id}:${product.variantId}`
			if (freeLines.some((line) => line.key === key)) return
			freeLines.push({ key, discountId: discount.id, title: discount.title, productId: product.productId, variantId: product.variantId, name: product.name, quantity: 1, originalUnitPrice: product.price })
			applied.add(discount.id)
		})
	})

	// ---- Coupon: one per order, never on free lines ----
	const couponShare: Record<string, Minor> = {}
	let couponResult: CouponResult | null = null
	if (input.coupon) {
		const coupon = input.coupon
		const hasLineDiscount = paidLines.some((line) => lineDiscount[line.key])
		const eligible = config.couponBlockedByLineDiscount === "order"
			? (hasLineDiscount ? [] : paidLines)
			: paidLines.filter((line) => !lineDiscount[line.key])
		const base = eligible.reduce((sum, line) => sum + lineTotal[line.key], 0)
		let reason: string | null = null
		if (!isWithinDates(coupon, now)) reason = coupon.startsAt != null && now < coupon.startsAt ? "it is not active yet" : "it has expired"
		else if (config.couponBlockedByLineDiscount === "order" && hasLineDiscount) reason = "it cannot be combined with item discounts"
		else if (!eligible.length) reason = "every item already has a discount"
		else if (!restrictionMet(coupon.restriction, eligible)) {
			reason = coupon.restriction.kind === "MIN_QUANTITY"
				? `it needs at least ${coupon.restriction.min} undiscounted items`
				: `it needs a minimum of ${formatAmount((coupon.restriction as { min: Minor }).min)} on undiscounted items`
		}
		let amount = 0
		if (!reason) {
			amount = coupon.valueType === "PERCENTAGE" ? percentOf(base, coupon.value) : coupon.value
			amount = Math.max(0, Math.min(amount, coupon.cap ?? Infinity, base))
			if (amount <= 0) reason = "there is nothing to discount"
		}
		if (reason) {
			amount = 0
			messages.push({ severity: "warning", discountId: coupon.id, text: `Coupon ${coupon.code} not applied: ${reason}.` })
		} else {
			const shares = allocate(amount, eligible.map((line) => lineTotal[line.key]))
			eligible.forEach((line, index) => { couponShare[line.key] = shares[index] })
			applied.add(coupon.id)
		}
		couponResult = { id: coupon.id, applied: !reason, amount, reason }
	}

	// ---- Cashier's additional discount, after the coupon ----
	const additionalShare: Record<string, Minor> = {}
	let additionalDiscount = 0
	if (input.additionalDiscount) {
		const weights = paidLines.map((line) => lineTotal[line.key] - (couponShare[line.key] ?? 0))
		const base = weights.reduce((sum, weight) => sum + weight, 0)
		const { type, value } = input.additionalDiscount
		additionalDiscount = Math.max(0, Math.min(type === "PERCENTAGE" ? percentOf(base, value) : value, base))
		const shares = allocate(additionalDiscount, weights)
		paidLines.forEach((line, index) => { additionalShare[line.key] = shares[index] })
	}

	// ---- Tax on discounted line values ----
	const pricedLines: PricedLine[] = lines.map((line) => {
		const gross = grossOf(line)
		const couponPart = couponShare[line.key] ?? 0
		const additionalPart = additionalShare[line.key] ?? 0
		const taxable = lineTotal[line.key] - couponPart - additionalPart
		const tax = line.taxes.filter((item) => !item.inclusive).reduce((sum, item) => sum + percentOf(taxable, item.rate), 0)
		const inclusiveRate = line.taxes.filter((item) => item.inclusive).reduce((sum, item) => sum + item.rate, 0)
		const inclusiveTax = inclusiveRate > 0 ? taxable - Math.round((taxable * 100) / (100 + inclusiveRate)) : 0
		return {
			key: line.key,
			productId: line.productId,
			variantId: line.variantId,
			name: line.name,
			quantity: line.quantity,
			unitPrice: line.unitPrice,
			gross,
			discount: lineDiscount[line.key] ?? null,
			lineTotal: lineTotal[line.key],
			couponShare: couponPart,
			additionalShare: additionalPart,
			taxable,
			tax,
			inclusiveTax,
			automaticOptions: optionsByLine[line.key] ?? [],
			selectedAutomaticId: lineDiscount[line.key]?.kind === "AUTOMATIC" ? lineDiscount[line.key]!.discountId : null,
			claimableOffers: claimableByLine[line.key] ?? [],
		}
	})

	// ---- Explain discounts that applied last time and no longer do ----
	const explained = new Set(messages.map((message) => message.discountId))
	input.previousAppliedDiscountIds?.forEach((id) => {
		if (applied.has(id) || explained.has(id)) return
		const discount = input.discounts.find((item) => item.id === id)
		if (!discount) return
		const state = activity.get(id)
		if (state === "NOT_ELIGIBLE") {
			messages.push({ severity: "warning", discountId: id, text: `"${discount.title}" removed: not available for ${customerId == null ? "walk-in customers" : "this customer"}.` })
		} else if (state === "EXPIRED" || state === "NOT_STARTED") {
			messages.push({ severity: "warning", discountId: id, text: `"${discount.title}" removed: it is no longer active.` })
		} else if (discount.kind === "AUTOMATIC" && !paidLines.some((line) => optionsByLine[line.key]?.some((option) => option.discountId === id))) {
			messages.push({ severity: "warning", discountId: id, text: `"${discount.title}" removed: its conditions are no longer met.` })
		}
	})

	const subtotal = paidLines.reduce((sum, line) => sum + grossOf(line), 0)
	const lineDiscountTotal = paidLines.reduce((sum, line) => sum + (lineDiscount[line.key]?.amount ?? 0), 0)
	const couponDiscount = couponResult?.amount ?? 0
	const totalDiscount = lineDiscountTotal + couponDiscount + additionalDiscount
	const discountedSubtotal = subtotal - totalDiscount
	const tax = pricedLines.reduce((sum, line) => sum + line.tax, 0)
	const inclusiveTax = pricedLines.reduce((sum, line) => sum + line.inclusiveTax, 0)
	const additionalCharge = input.additionalCharge
		? Math.max(0, input.additionalCharge.type === "PERCENTAGE" ? percentOf(subtotal, input.additionalCharge.value) : input.additionalCharge.value)
		: 0
	const tip = Math.max(0, input.tip)
	const grandTotal = discountedSubtotal + tax + additionalCharge + tip
	// Round off to the nearest whole unit (₹1 = 100 Minor); halves round up
	const payableTotal = config.roundOffSale ? Math.round(grandTotal / MINOR_PER_MAJOR) * MINOR_PER_MAJOR : grandTotal

	return {
		lines: pricedLines,
		freeLines,
		claims,
		coupon: couponResult,
		appliedDiscountIds: [...applied],
		totals: {
			quantity: paidLines.reduce((sum, line) => sum + line.quantity, 0),
			subtotal,
			lineDiscount: lineDiscountTotal,
			subtotalAfterLineDiscounts,
			couponDiscount,
			additionalDiscount,
			totalDiscount,
			discountedSubtotal,
			tax,
			inclusiveTax,
			additionalCharge,
			tip,
			grandTotal,
			roundOff: payableTotal - grandTotal,
			payableTotal,
		},
		messages,
	}
}
